/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Alert,
  Button,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core"
import { t } from "i18next"
import { notifications } from "@mantine/notifications"
import { useState } from "react"
import { useQueryGet } from "@/typed-client/hooks/use-query-get"
import { DefaultModal } from "@components/default-modal"

interface RoyaltiesCalculationSummary {
  settlementCount: number
  positionCount: number
  activityCount: number
  netTotal: number
  taxTotal: number
  grossTotal: number
}

interface CalculateRoyaltiesModalProps {
  opened: boolean
  onClose: () => void
  yearMonth: {
    year: number
    month: number
  }
}

type ApiRoyaltyPosition = {
  activityId?: number
  ActivityId?: number
  totelTicketsCount?: number
  TotelTicketsCount?: number
  royaltyTicketsCount?: number
  RoyaltyTicketsCount?: number
  totalTicketPrice?: number
  TotalTicketPrice?: number
  royaltyTicketPrice?: number
  RoyaltyTicketPrice?: number
  totalTicketFees?: number
  TotalTicketFees?: number
  royaltyAmount?: number
  RoyaltyAmount?: number
}

type ApiRoyaltyRuleBlock = {
  positions?: ApiRoyaltyPosition[]
  Positions?: ApiRoyaltyPosition[]
}

type ApiRoyaltySettlement = {
  netPrice?: number
  NetPrice?: number
  taxes?: number
  Taxes?: number
  endPrice?: number
  EndPrice?: number
  rulePositions?: ApiRoyaltyRuleBlock[]
  RulePositions?: ApiRoyaltyRuleBlock[]
}

type ActivityRow = {
  activityId: number
  totalTicketsCount: number
  royaltyTicketsCount: number
  totalTicketPrice: number
  royaltyTicketPrice: number
  totalTicketFees: number
  royaltyAmount: number
}

const getNumber = (value: unknown) => (typeof value === "number" ? value : 0)

const toActivityRows = (data: unknown): ActivityRow[] => {
  if (!Array.isArray(data)) {
    return []
  }

  const rowsByActivity = new Map<number, ActivityRow>()
  const settlements = data as ApiRoyaltySettlement[]

  for (const settlement of settlements) {
    const ruleBlocks =
      settlement.rulePositions ?? settlement.RulePositions ?? []
    for (const ruleBlock of ruleBlocks) {
      const positions = ruleBlock.positions ?? ruleBlock.Positions ?? []
      for (const position of positions) {
        const activityId = position.activityId ?? position.ActivityId
        if (typeof activityId !== "number") {
          continue
        }

        const positionData = {
          totalTicketsCount: getNumber(
            position.totelTicketsCount ?? position.TotelTicketsCount
          ),
          royaltyTicketsCount: getNumber(
            position.royaltyTicketsCount ?? position.RoyaltyTicketsCount
          ),
          totalTicketPrice: getNumber(
            position.totalTicketPrice ?? position.TotalTicketPrice
          ),
          royaltyTicketPrice: getNumber(
            position.royaltyTicketPrice ?? position.RoyaltyTicketPrice
          ),
          totalTicketFees: getNumber(
            position.totalTicketFees ?? position.TotalTicketFees
          ),
          royaltyAmount: getNumber(
            position.royaltyAmount ?? position.RoyaltyAmount
          ),
        }

        const existing = rowsByActivity.get(activityId)
        if (existing) {
          existing.totalTicketsCount += positionData.totalTicketsCount
          existing.royaltyTicketsCount += positionData.royaltyTicketsCount
          existing.totalTicketPrice += positionData.totalTicketPrice
          existing.royaltyTicketPrice += positionData.royaltyTicketPrice
          existing.totalTicketFees += positionData.totalTicketFees
          existing.royaltyAmount += positionData.royaltyAmount
          continue
        }

        rowsByActivity.set(activityId, {
          activityId,
          ...positionData,
        })
      }
    }
  }

  return Array.from(rowsByActivity.values()).sort(
    (a, b) => a.activityId - b.activityId
  )
}

const asSummary = (data: unknown): RoyaltiesCalculationSummary | null => {
  if (!data || typeof data !== "object") {
    return null
  }

  const maybeSummary = data as Partial<RoyaltiesCalculationSummary>
  if (
    typeof maybeSummary.settlementCount === "number" &&
    typeof maybeSummary.positionCount === "number" &&
    typeof maybeSummary.activityCount === "number"
  ) {
    return {
      settlementCount: maybeSummary.settlementCount,
      positionCount: maybeSummary.positionCount,
      activityCount: maybeSummary.activityCount,
      netTotal: getNumber(maybeSummary.netTotal),
      taxTotal: getNumber(maybeSummary.taxTotal),
      grossTotal: getNumber(maybeSummary.grossTotal),
    }
  }

  if (!Array.isArray(data)) {
    return null
  }

  const settlements = data as ApiRoyaltySettlement[]
  let settlementCount = 0
  let positionCount = 0
  let netTotal = 0
  let taxTotal = 0
  let grossTotal = 0
  const activityIds = new Set<number>()

  for (const settlement of settlements) {
    const ruleBlocks =
      settlement.rulePositions ?? settlement.RulePositions ?? []

    let hasPositions = false
    for (const ruleBlock of ruleBlocks) {
      const positions = ruleBlock.positions ?? ruleBlock.Positions ?? []
      if (positions.length > 0) {
        hasPositions = true
        positionCount += positions.length
        for (const position of positions) {
          const activityId = position.activityId ?? position.ActivityId
          if (typeof activityId === "number") {
            activityIds.add(activityId)
          }
        }
      }
    }

    if (hasPositions) {
      settlementCount++
    }

    netTotal += getNumber(settlement.netPrice ?? settlement.NetPrice)
    taxTotal += getNumber(settlement.taxes ?? settlement.Taxes)
    grossTotal += getNumber(settlement.endPrice ?? settlement.EndPrice)
  }

  return {
    settlementCount,
    positionCount,
    activityCount: activityIds.size,
    netTotal,
    taxTotal,
    grossTotal,
  }
}

const moneyFormatter = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
})

function CalculateRoyaltiesModalContent({
  onClose,
  yearMonth,
}: CalculateRoyaltiesModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<RoyaltiesCalculationSummary | null>(null)
  const [activityRows, setActivityRows] = useState<ActivityRow[]>([])

  const { refetch } = useQueryGet<any>(
    "/api/ActicitySale/GetRoyaltiesCalculationSummary" as any,
    {
      yearmonth: `${yearMonth.year}-${String(yearMonth.month).padStart(2, "0")}`,
    } as any,
    { enabled: false }
  )

  const handleCalculate = async () => {
    setIsLoading(true)

    try {
      const response = await refetch()
      const summary = asSummary(response.data)
      const rows = toActivityRows(response.data)

      if (!summary) {
        notifications.show({
          color: "red",
          title: t("Error"),
          message: t("CouldNotProcessResponse"),
        })
        return
      }

      setResult(summary)
      setActivityRows(rows)
      notifications.show({
        color: "green",
        title: t("CalculationCompleted"),
        message: t("CalculationCompletedMessage", {
          month: yearMonth.month,
          year: yearMonth.year,
        }),
      })
    } catch {
      notifications.show({
        color: "red",
        title: t("Error"),
        message: t("CalculationFailed"),
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack gap="md">
      <Alert color="blue" title={t("CalculationScope")}>
        {t("CalculationScopeDescription")}
      </Alert>

      {result && (
        <Alert color="green" title={t("Result")}>
          <Stack gap={4}>
            <Text size="sm">
              {t("Settlements")}: {result.settlementCount}
            </Text>
            <Text size="sm">
              {t("RulePositions")}: {result.positionCount}
            </Text>
            <Text size="sm">
              {t("Activities")}: {result.activityCount}
            </Text>
            <Text size="sm">
              {t("Net")}: {moneyFormatter.format(result.netTotal ?? 0)}
            </Text>
            <Text size="sm">
              {t("Tax")}: {moneyFormatter.format(result.taxTotal ?? 0)}
            </Text>
            <Text fw={600} size="sm">
              {t("Gross")}: {moneyFormatter.format(result.grossTotal ?? 0)}
            </Text>
          </Stack>
        </Alert>
      )}

      {activityRows.length > 0 && (
        <Alert color="blue" title={t("DetailsPerActivity")}>
          <Stack gap="xs">
            <Text size="sm">{t("DetailsPerActivityDescription")}</Text>

            <ScrollArea>
              <Table withTableBorder withColumnBorders striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("Activity")}</Table.Th>
                    <Table.Th>{t("TicketsTotal")}</Table.Th>
                    <Table.Th>{t("TicketsRoyalty")}</Table.Th>
                    <Table.Th>{t("RevenueTotal")}</Table.Th>
                    <Table.Th>{t("RevenueRoyalty")}</Table.Th>
                    <Table.Th>{t("FeesTotal")}</Table.Th>
                    <Table.Th>{t("Royalty")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {activityRows.map((row) => (
                    <Table.Tr key={row.activityId}>
                      <Table.Td>{row.activityId}</Table.Td>
                      <Table.Td>{row.totalTicketsCount}</Table.Td>
                      <Table.Td>{row.royaltyTicketsCount}</Table.Td>
                      <Table.Td>
                        {moneyFormatter.format(row.totalTicketPrice)}
                      </Table.Td>
                      <Table.Td>
                        {moneyFormatter.format(row.royaltyTicketPrice)}
                      </Table.Td>
                      <Table.Td>
                        {moneyFormatter.format(row.totalTicketFees)}
                      </Table.Td>
                      <Table.Td>
                        {moneyFormatter.format(row.royaltyAmount)}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  <Table.Tr>
                    <Table.Td>
                      <Text fw={700}>{t("Sum")}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {activityRows.reduce(
                          (sum, row) => sum + row.totalTicketsCount,
                          0
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {activityRows.reduce(
                          (sum, row) => sum + row.royaltyTicketsCount,
                          0
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {moneyFormatter.format(
                          activityRows.reduce(
                            (sum, row) => sum + row.totalTicketPrice,
                            0
                          )
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {moneyFormatter.format(
                          activityRows.reduce(
                            (sum, row) => sum + row.royaltyTicketPrice,
                            0
                          )
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {moneyFormatter.format(
                          activityRows.reduce(
                            (sum, row) => sum + row.totalTicketFees,
                            0
                          )
                        )}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={700}>
                        {moneyFormatter.format(
                          activityRows.reduce(
                            (sum, row) => sum + row.royaltyAmount,
                            0
                          )
                        )}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        </Alert>
      )}

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose} disabled={isLoading}>
          {t("Close")}
        </Button>
        <Button color="teal" onClick={handleCalculate} loading={isLoading}>
          {t("CalculateNow")}
        </Button>
      </Group>
    </Stack>
  )
}
export function CalculateRoyaltiesModal(props: CalculateRoyaltiesModalProps) {
  return (
    <DefaultModal
      opened={props.opened}
      onClose={props.onClose}
      title={t("CalculateRoyalties")}
      size="xl"
    >
      <CalculateRoyaltiesModalContent {...props} />
    </DefaultModal>
  )
}
